import { useState } from "react";
import {
  Flex,
  Heading,
  Input,
  Button,
  InputGroup,
  Stack,
  InputLeftElement,
  Box,
  Avatar,
  FormControl,
  Text,
  useToast,
  FormErrorMessage
} from "@chakra-ui/react";
import { PhoneIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import { login } from '../apis/loginApi';

const Auth = () => {
  const [loginType, setLoginType] = useState("sms");
  const [userLogin, setUserLogin] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();

  const handleUserChange = (e) => {
    let inputValue = e.target.value;
    setError("");
    
    // Remove non-digit characters from the input
    inputValue = inputValue.replace(/\D/g, '');

    // Check if the length is more than 10
    if (inputValue.length > 10) {
      return; // Don't update the state if the length is more than 10
    }

    // Apply the phone number format
    inputValue = inputValue.replace(/(\d{3})(\d{0,3})(\d{0,4})/, (_, p1, p2, p3) => {
      let formattedNumber = p1;
      if (p2) formattedNumber += `-${p2}`;
      if (p3) formattedNumber += `-${p3}`;
      return formattedNumber;
    });
    
    setUserLogin(inputValue);
  };

  const handleClearInput = () => {
    setUserLogin('');
    setError("");
  };

  const validateForm = () => {
    if (!userLogin.trim()) {
      setError('Phone number is required');
      return false;
    }
    
    if (userLogin.replace(/\D/g, '').length !== 10) {
      setError('Phone number must be 10 digits');
      return false;
    }
    
    return true;
  };

  const loginSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast({
        title: "Form validation error",
        description: "Please check your phone number and try again",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await login(userLogin, loginType);
      if (response.success === true) {
        toast({
          title: "Login request successful",
          description: "Please verify your account with the OTP code sent to you",
          status: "success",
          duration: 1000,
          isClosable: true,
          position: "top",
        });
        navigate(response.redirectPath); // Navigate to the path received from the backend
      } else {
        toast({
          title: "Login failed",
          description: response.message || "There was an error with your login. Please try again.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
        navigate(response.redirectPath);
      }
    } catch (error) {
      toast({
        title: "Login failed",
        description: error.message || "There was an error with your login. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      if (error.redirectPath) {
        navigate(error.redirectPath);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Flex
      flexDirection="column"
      width="100wh"
      height="100vh"
      backgroundColor="#edf3f8"
      justifyContent="center"
      alignItems="center"
    >
      <Stack
        flexDir="column"
        mt="-20%"
        justifyContent="center"
        alignItems="center"
      >
        <Avatar bg="#82ca9c" />
        <Heading color="#093030">Login Or Sign Up</Heading>
        <Text fontSize="md" color="gray.600">Don't have an account? Just continue below!</Text>
        <Box minW={{ base: "90%", md: "468px" }}>
          <form onSubmit={loginSubmit}>
            <Stack
              p="2rem"
              backgroundColor="white"
              boxShadow="0 4px 12px rgba(0, 0, 0, 0.1)"
              borderRadius="lg"
              border="1px solid #E2E8F0"
              spacing={6}
            >
              <FormControl isInvalid={!!error}>
                <InputGroup>
                  <InputLeftElement
                    pointerEvents="none"
                    children={<PhoneIcon color="#CBD5E0" />}
                  />
                  <Input
                    type="tel"
                    placeholder="Phone Number"
                    value={userLogin}
                    onChange={handleUserChange}
                    borderRadius="md"
                    _focus={{ borderColor: "#82ca9c", boxShadow: "0 0 0 1px #82ca9c" }}
                  />
                </InputGroup>
                <FormErrorMessage fontSize="xs">{error}</FormErrorMessage>
              </FormControl>

              <Button
                type="submit"
                variant="solid"
                bg="#82ca9c"
                color="white"
                width="full"
                borderRadius="md"
                _hover={{ bg: "#6db889" }}
                _active={{ bg: "#5ba97c" }}
                boxShadow="0 2px 4px rgba(130, 202, 156, 0.3)"
                isLoading={isSubmitting}
                loadingText="Submitting"
              >
                Login / Sign Up
              </Button>
            </Stack>
          </form>
        </Box>
      </Stack>
    </Flex>
  );
};

export default Auth;